import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, Lock, Send } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import type { TicketDetail } from '@aurora/contracts';
import { get, patch, post, type ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { shortDate, textToHtml } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useStatuses } from '../../lib/directory';
import { describeError, useToast } from '../../components/toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/tabs';
import { Button, Textarea } from '../../components/ui';
import { AttachmentPicker, useAttachmentUpload, type AttachmentUpload } from './attachments';
import {
  InsertResponseButton,
  ResponseList,
  renderResponse,
  slashTokenAt,
  useResponseRows,
  type CannedResponse,
} from './composer-canned';
import { firstOfCategory, refreshTicket } from './detail-data';

/**
 * Writing on a ticket.
 *
 * A reply and an internal note are separate drafts with separate files. They
 * used to share one box, so a note typed under "Internal note" became an email
 * to the requester with one wrong click — "the expensive mistake on this
 * screen". Each draft is kept per ticket in session storage, so it survives a
 * reload or a look at another ticket, and never follows the agent to the next
 * one.
 *
 * A message is posted with its files in one request: the server attaches them
 * in the same transaction, so a reply can no longer go out while its files
 * fail, and the obvious response — Send again — no longer emails it twice.
 */

type Kind = 'reply' | 'note';

/** A draft that survives a reload, per ticket and per kind. */
export function usePersistentDraft(key: string): [string, (value: string) => void] {
  const [value, setValue] = useState<string>(() => {
    try {
      return window.sessionStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  });

  const update = (next: string) => {
    setValue(next);
    try {
      if (next) window.sessionStorage.setItem(key, next);
      else window.sessionStorage.removeItem(key);
    } catch {
      // Storage can be unavailable (private windows, blocked site data); the
      // draft still lives in memory for as long as the page is open.
    }
  };

  return [value, update];
}

export const draftKey = (number: number, kind: Kind) => `aurora:ticket-draft:${number}:${kind}`;

export function Composer({
  ticket,
  reopenWindowDays,
  onSent,
}: {
  ticket: TicketDetail;
  reopenWindowDays: number;
  onSent?: () => void;
}) {
  const { session, can } = useSession();
  const isAgent = Boolean(session?.is_agent);
  const canNote = isAgent && can('ticket.note.create');
  const [kind, setKind] = useState<Kind>('reply');

  const [replyDraft, setReplyDraft] = usePersistentDraft(draftKey(ticket.number, 'reply'));
  const [noteDraft, setNoteDraft] = usePersistentDraft(draftKey(ticket.number, 'note'));
  const replyUpload = useAttachmentUpload();
  const noteUpload = useAttachmentUpload();

  // The starting template — agents' replies only, never a note, and only into
  // an empty box: a draft already typed, or deliberately cleared, wins.
  const template = useQuery<{ data: { body_text: string } | null }, ApiError>({
    queryKey: ['reply-template', ticket.number],
    queryFn: () => get(`/tickets/${ticket.number}/reply-template`),
    enabled: isAgent,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const templateText = template.data?.data?.body_text ?? '';
  const [templateUsed, setTemplateUsed] = useState(false);
  useEffect(() => {
    if (!isAgent || templateUsed || !templateText) return;
    setTemplateUsed(true);
    if (!replyDraft) setReplyDraft(templateText);
  }, [isAgent, templateUsed, templateText, replyDraft, setReplyDraft]);

  // Leaving with words typed or files still uploading asks first.
  const dirty =
    (replyDraft.trim() !== '' && replyDraft !== templateText) ||
    noteDraft.trim() !== '' ||
    replyUpload.busy ||
    noteUpload.busy ||
    replyUpload.pending.length > 0 ||
    noteUpload.pending.length > 0;
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const finished = ticket.status.category === 'resolved' || ticket.status.category === 'closed';

  if (!isAgent && finished) {
    const finishedAt = ticket.resolved_at ? new Date(ticket.resolved_at).getTime() : null;
    const expired = finishedAt !== null && Date.now() - finishedAt > reopenWindowDays * 86_400_000;
    if (expired) {
      return (
        <div className="rounded-card border border-line bg-sunken px-4 py-3 text-body text-ink-muted">
          This request was closed on {shortDate(ticket.resolved_at)}, too long ago to reopen it with a reply.{' '}
          <Link to="/tickets/new" className="font-medium text-accent-ink hover:underline">
            Raise a new request
          </Link>{' '}
          if you still need help.
        </div>
      );
    }
  }

  const replyPane = (
    <ComposerPane
      ticket={ticket}
      kind="reply"
      isAgent={isAgent}
      draft={replyDraft}
      setDraft={setReplyDraft}
      upload={replyUpload}
      onSent={() => {
        // A sent reply starts the box from the template again.
        setReplyDraft(isAgent ? templateText : '');
        onSent?.();
      }}
      hint={
        !isAgent && finished
          ? 'Replying will reopen this request.'
          : isAgent
            ? 'The requester is emailed this reply.'
            : 'The service desk will see this and reply here.'
      }
      templateText={isAgent ? templateText : ''}
    />
  );

  if (!canNote) {
    return (
      <section aria-labelledby="composer-heading">
        <h2 id="composer-heading" className="mb-2 text-reading font-semibold text-ink">
          {isAgent ? 'Reply' : 'Add a reply'}
        </h2>
        {replyPane}
      </section>
    );
  }

  return (
    <section aria-label="Write on this ticket">
      <Tabs value={kind} onValueChange={(value) => setKind(value as Kind)}>
        <TabsList className="mb-2">
          <TabsTrigger value="reply">
            <Send aria-hidden className="size-3.5" />
            Reply
            {replyDraft.trim() && replyDraft !== templateText && kind !== 'reply' && (
              <span className="size-1.5 rounded-full bg-accent" aria-label="(draft)" />
            )}
          </TabsTrigger>
          <TabsTrigger value="note">
            <Lock aria-hidden className="size-3.5" />
            Internal note
            {noteDraft.trim() && kind !== 'note' && (
              <span className="size-1.5 rounded-full bg-caution" aria-label="(draft)" />
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="reply">{replyPane}</TabsContent>
        <TabsContent value="note">
          <ComposerPane
            ticket={ticket}
            kind="note"
            isAgent
            draft={noteDraft}
            setDraft={setNoteDraft}
            upload={noteUpload}
            onSent={() => {
              setNoteDraft('');
              onSent?.();
            }}
            hint="Only agents can see a note. It does not count as a response to the requester."
            templateText=""
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}

type FollowUp = 'pending' | 'resolve';

function ComposerPane({
  ticket,
  kind,
  isAgent,
  draft,
  setDraft,
  upload,
  onSent,
  hint,
  templateText,
}: {
  ticket: TicketDetail;
  kind: Kind;
  isAgent: boolean;
  draft: string;
  setDraft: (value: string) => void;
  upload: AttachmentUpload;
  onSent: () => void;
  hint: string;
  templateText: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const statuses = useStatuses(isAgent && kind === 'reply');
  const textarea = useRef<HTMLTextAreaElement>(null);
  const listId = useId();
  const hintId = useId();
  const [error, setError] = useState<string | null>(null);
  const [unresolved, setUnresolved] = useState<string[]>([]);
  const [slash, setSlash] = useState<{ query: string; start: number } | null>(null);
  const [slashActive, setSlashActive] = useState(0);
  // Opened by clicking into it; closed again only once something is sent, never
  // on blur — a box that shrank as the pointer went down on "Attach" or "Insert
  // response" moved the button out from under the click.
  const [opened, setOpened] = useState(false);
  const canUseResponses = isAgent && can('ticket.canned_response.use');
  const slashRows = useResponseRows(slash?.query ?? '', Boolean(slash) && canUseResponses);
  const number = ticket.number;

  const pendingStatus = firstOfCategory(statuses.data, 'pending');
  const resolvedStatus = firstOfCategory(statuses.data, 'resolved');
  const finished = ticket.status.category === 'resolved' || ticket.status.category === 'closed';

  const send = useMutation<
    { reopened: boolean; followUpFailed: ApiError | null },
    ApiError,
    { then?: FollowUp }
  >({
    mutationFn: async ({ then }) => {
      const text = draft;
      const attachmentIds = upload.ids();
      if (then === 'resolve' && resolvedStatus) {
        // One request: the reply goes out as the resolution note, in the same
        // transaction as the status, so a refusal sends nothing at all.
        await patch(`/tickets/${number}`, {
          status: resolvedStatus.key,
          resolution_note: text,
          ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
        });
        return { reopened: false, followUpFailed: null };
      }

      const result = await post<{ data: { id: number; reopened?: boolean } }>(`/tickets/${number}/messages`, {
        kind,
        body_html: textToHtml(text),
        body_text: text,
        ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
      });

      let followUpFailed: ApiError | null = null;
      if (then === 'pending' && pendingStatus) {
        try {
          await patch(`/tickets/${number}`, { status: pendingStatus.key });
        } catch (failure) {
          followUpFailed = failure as ApiError;
        }
      }
      return { reopened: Boolean(result.data?.reopened), followUpFailed };
    },
    onMutate: () => setError(null),
    onSuccess: (result, { then }) => {
      upload.clear();
      setUnresolved([]);
      // Back to its small size unless the writer is still in it (Ctrl+Enter).
      if (document.activeElement !== textarea.current) setOpened(false);
      onSent();
      refreshTicket(queryClient, String(number));
      if (result.followUpFailed) {
        toast.error('Reply sent, but the status was not changed', describeError(result.followUpFailed).title);
      } else if (then === 'resolve') {
        toast.success(`Reply sent and #${number} resolved`);
      } else if (then === 'pending') {
        toast.success('Reply sent', `#${number} is waiting on the requester.`);
      } else if (kind === 'note') {
        toast.success('Note added');
      } else if (result.reopened) {
        toast.success('Reply sent', 'Your request has been reopened.');
      } else {
        toast.success(isAgent ? 'Reply sent' : 'Reply added');
      }
    },
    onError: (failure) => setError(describeError(failure).title),
  });

  const canSend = draft.trim() !== '' && !upload.busy && !send.isPending;
  const offerPending = Boolean(isAgent && kind === 'reply' && pendingStatus && ticket.status.key !== pendingStatus.key);
  const offerResolve = Boolean(isAgent && kind === 'reply' && resolvedStatus && !finished);
  const split = offerPending || offerResolve;

  const insert = async (response: CannedResponse, range?: { start: number; end: number }) => {
    try {
      const rendered = await renderResponse(response.id, number);
      const element = textarea.current;
      const start = range?.start ?? element?.selectionStart ?? draft.length;
      const end = range?.end ?? element?.selectionEnd ?? draft.length;
      const next = draft.slice(0, start) + rendered.text + draft.slice(end);
      setDraft(next);
      setUnresolved(rendered.unresolved);
      requestAnimationFrame(() => {
        if (!element) return;
        element.focus();
        const caret = start + rendered.text.length;
        element.setSelectionRange?.(caret, caret);
      });
    } catch (failure) {
      toast.error(failure);
    }
  };

  const onChange = (value: string, caret: number) => {
    setDraft(value);
    if (canUseResponses) {
      const token = slashTokenAt(value, caret);
      setSlash(token);
      // Every letter narrows the list, so the choice goes back to its first
      // row rather than pointing past the end of a shorter one.
      setSlashActive(0);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slash && slashRows.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSlashActive((current) => Math.min(current + 1, slashRows.length - 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSlashActive((current) => Math.max(current - 1, 0));
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const chosen = slashRows[slashActive];
        const token = slash;
        setSlash(null);
        if (chosen) void insert(chosen, { start: token.start, end: token.start + token.query.length + 1 });
        return;
      }
    }
    if (slash && event.key === 'Escape') {
      event.preventDefault();
      setSlash(null);
      return;
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      if (canSend) send.mutate({});
    }
  };

  const isNote = kind === 'note';
  const label = isNote ? 'Internal note' : isAgent ? 'Reply to the requester' : 'Your reply';

  /**
   * Two lines while nobody is using it — nothing typed beyond the starting
   * template, nothing attached — so the conversation it sits under keeps the
   * room. On a laptop screen the open box took two fifths of the height and
   * left the conversation one message tall. It opens the moment it is clicked.
   */
  const idle =
    !opened && (draft.trim() === '' || draft === templateText) && upload.pending.length === 0 && !upload.busy;

  return (
    <div
      className={cn(
        'rounded-card border bg-surface p-3 shadow-raised',
        isNote ? 'border-caution-line bg-caution-soft' : 'border-line',
      )}
    >
      <div className="relative">
        <Textarea
          ref={textarea}
          value={draft}
          onChange={(event) => onChange(event.target.value, event.target.selectionStart ?? event.target.value.length)}
          onKeyDown={onKeyDown}
          onFocus={() => setOpened(true)}
          onBlur={() => setSlash(null)}
          rows={idle ? 2 : 5}
          aria-label={label}
          aria-describedby={hintId}
          aria-autocomplete={canUseResponses ? 'list' : undefined}
          aria-controls={slash ? listId : undefined}
          aria-activedescendant={slash && slashRows.length > 0 ? `${listId}-${slashActive}` : undefined}
          placeholder={
            isNote
              ? 'Write a note for the desk. The requester never sees it.'
              : isAgent
                ? `Write a reply${canUseResponses ? ' — type / to insert a canned response' : ''}`
                : 'Add details or answer the question you were asked'
          }
          className={cn('max-h-80 resize-y text-reading', idle ? 'min-h-0' : 'min-h-28')}
        />
        {slash && canUseResponses && (
          // Above the box, not below it: the reply box sits at the bottom of
          // the screen, and a list opened downwards ran off it and under the
          // send button.
          <div
            data-testid="slash-responses"
            className="absolute inset-x-0 bottom-full z-20 mb-1 max-h-64 overflow-y-auto rounded-card border border-line bg-raised p-1 shadow-float"
          >
            <ResponseList
              query={slash.query}
              activeIndex={slashActive}
              onActiveIndexChange={setSlashActive}
              listId={listId}
              onPick={(response) => {
                const token = slash;
                setSlash(null);
                void insert(response, { start: token.start, end: token.start + token.query.length + 1 });
              }}
            />
          </div>
        )}
      </div>

      {unresolved.length > 0 && (
        <p className="mt-2 flex items-start gap-1.5 text-body text-caution-ink">
          <AlertTriangle aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          <span>
            This ticket could not fill {unresolved.map((name) => `{{${name}}}`).join(', ')} — replace{' '}
            {unresolved.length === 1 ? 'it' : 'them'} before sending.
          </span>
        </p>
      )}

      {error && (
        <p role="alert" className="mt-2 rounded-control border border-critical-line bg-critical-soft px-3 py-2 text-body text-critical-ink">
          {error}
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2">
        <AttachmentPicker upload={upload} disabled={send.isPending} />
        {canUseResponses && <InsertResponseButton onPick={(response) => void insert(response)} disabled={send.isPending} />}
        {!isNote && isAgent && templateText && draft === templateText && (
          <Button size="sm" variant="ghost" onClick={() => setDraft('')}>
            Clear the template
          </Button>
        )}
      </div>

      {/* The hint wraps inside its own column rather than pushing the send
          button onto a line of its own: that cost the conversation a row of
          height on a laptop screen, and moved the button the moment the
          statuses loaded and its menu half appeared. */}
      <div className="mt-2 flex items-center justify-between gap-2 border-t border-line pt-2">
        <p id={hintId} className="min-w-0 flex-1 text-mini text-ink-muted">
          {hint} <span className="hidden sm:inline">Ctrl+Enter sends.</span>
        </p>
        <div className="flex shrink-0 items-center">
          <Button
            variant={isNote ? 'secondary' : 'primary'}
            loading={send.isPending}
            disabled={!canSend}
            onClick={() => send.mutate({})}
            className={cn(split && 'rounded-e-none')}
          >
            {upload.busy ? 'Uploading…' : isNote ? 'Add note' : isAgent ? 'Send reply' : 'Send'}
          </Button>
          {split && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant="primary"
                  disabled={!canSend}
                  aria-label="More ways to send"
                  className="rounded-s-none border-s border-white/20 px-2"
                >
                  <ChevronDown aria-hidden className="size-3.5" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={4}
                  className="animate-pop z-50 min-w-52 rounded-card border border-line bg-raised p-1 shadow-float"
                >
                  {offerPending && pendingStatus && (
                    <DropdownMenu.Item
                      onSelect={() => send.mutate({ then: 'pending' })}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-ink outline-none data-highlighted:bg-sunken"
                    >
                      Send and set {pendingStatus.name}
                    </DropdownMenu.Item>
                  )}
                  {offerResolve && (
                    <DropdownMenu.Item
                      onSelect={() => send.mutate({ then: 'resolve' })}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-ink outline-none data-highlighted:bg-sunken"
                    >
                      Send and resolve
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>
      </div>
    </div>
  );
}
